/**
 * /fb/pricing - the Facebook-funnel paywall.
 *
 * Reached from /fb/onboarding after the email step (answers handed over via
 * sessionStorage 'ttt_fb_answers'). Styled after the big competitors' paywalls
 * (Speech Blubs / Keiki): a strong value proposition up top, benefit bullets,
 * social proof + rating, a single clear plan card, one CTA, and legal footer.
 *
 * Pricing here is the same single product as the rest of the app: charged
 * immediately, no trial - EUR 11.49 / USD 13.49 / GBP 9.99 per month. It uses
 * its own Stripe Payment Link (FB_STRIPE_LINK) only so the success redirect can
 * land on /fb/success. The captured email is pre-filled at checkout so the
 * webhook can map the resulting Stripe customer back to the lead by email.
 *
 * Abandoned checkout: /fb/success consumes and clears the 'ttt_fb_purchase'
 * stash after a paid return, so if the stash is still here on mount the visitor
 * went to Stripe and came back without paying. We fire 'payment_abandoned' (the
 * drop was invisible in PostHog before this). No consolation offer is shown:
 * there is one price and it is billed today.
 *
 * Skin + font are shared with the onboarding funnel via FbShell's FB tokens.
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Check, Star, ShieldCheck, Lightning, Heart, CaretDown } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import posthog from 'posthog-js';
import { trackPixel } from '@/lib/metaPixel';
import { CURRENCY_OPTIONS } from '@/lib/stripeLinks';
import { useFbFunnelLang } from '@/lib/useFbFunnelLang';
import { isInAppBrowser, breakOutToExternalBrowser } from '@/lib/inAppBrowser';
import { useAuth } from '@/contexts/AuthContext';
import { COPY, fill } from './copy';
import { FB, FbButton } from '@/pages/FbOnboarding/components/FbShell';
import { ImageSlot } from '@/pages/FbOnboarding/components/FbShell';
import timoWithKid from '@/assets/timo-with-kid.webp';
import founderPhoto from '@/assets/founder.webp';

// Pick the display currency from the visitor's LOCATION so a US parent sees $
// and a UK parent sees £ without touching the switch. We use the device
// timezone, not the browser language: language is a preference (a Lithuanian
// with an English-UK browser is still in Lithuania and must see EUR, not GBP).
// No network call, instant, private; the manual switch still overrides.
const EURO_REGIONS = new Set(['AT','BE','CY','EE','FI','FR','DE','GR','IE','IT','LV','LT','LU','MT','NL','PT','SK','SI','ES','HR']);
function guessCurrency() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz === 'Europe/London') return 'GBP';
    if (tz.startsWith('Europe/') || tz.startsWith('Atlantic/')) return 'EUR';
    if (tz.startsWith('America/')) return 'USD';
    if (tz) return 'USD'; // Asia / Australia / Pacific / Africa: USD is the closest of our three
  } catch { /* ignore */ }
  // Fallback only if timezone is unavailable: locale region (less reliable).
  try {
    const region = new Intl.Locale(navigator.language || 'en').maximize().region || '';
    if (region === 'GB') return 'GBP';
    if (EURO_REGIONS.has(region)) return 'EUR';
    if (region) return 'USD';
  } catch { /* ignore */ }
  return 'EUR';
}

// The FB funnel's own copy of the one product's Payment Link. Same price, same
// no-trial terms as the main paywall; it exists only because its success URL
// points at /fb/success (which fires the Meta Purchase pixel) instead of
// /success.
const FB_STRIPE_LINK = 'https://buy.stripe.com/eVqcN58091uNeo6evdbMQ07';

// Prices come from src/lib/stripeLinks.js so this funnel cannot drift off the
// real charge. Billed today, every month, no trial.
const CURRENCY = {
  EUR: { symbol: '€', price: CURRENCY_OPTIONS.EUR.price, per: '/month' },
  USD: { symbol: '$', price: CURRENCY_OPTIONS.USD.price, per: '/month' },
  GBP: { symbol: '£', price: CURRENCY_OPTIONS.GBP.price, per: '/month' },
};

const track = (event, props) => {
  try {
    posthog.capture(event, props);
  } catch {
    /* best-effort */
  }
};

const BENEFITS = [
  { icon: Lightning, text: 'Personalized speech & conversation practice, built around your answers' },
  { icon: Heart, text: 'Gentle real-time AI support that meets your child at their pace' },
  { icon: ShieldCheck, text: 'Guided by a speech and autism-communication framework' },
  { icon: Check, text: 'Works on any phone or tablet. Cancel anytime.' },
];

const REVIEWS = [
  {
    stars: 5,
    text: 'My son said his first full sentence after three weeks. I cried. Timo made him want to talk.',
    author: 'Rasa, mum of a 4-year-old',
  },
  {
    stars: 5,
    text: 'The only app my daughter actually asks for. Her words have exploded.',
    author: 'Michael, dad of a 3-year-old',
  },
];

// Read once, at module load: which language + source the visitor arrived with.
// The ORGANIC landing sends ?src=landing&lang=xx; the paid ads funnel sends
// neither and must stay English (or its /fb/:lang path language). Reading from
// window.location keeps FbPricing free of router search hooks, matching how the
// rest of this file is written.
function readEntry() {
  try {
    const p = new URLSearchParams(window.location.search);
    return { src: p.get('src') || null, lang: p.get('lang') || null };
  } catch {
    return { src: null, lang: null };
  }
}

export default function FbPricing() {
  const { funnelLang } = useFbFunnelLang();
  const { user } = useAuth();
  const [answers, setAnswers] = useState(null);
  const [currency, setCurrency] = useState(() => guessCurrency());
  const [storyOpen, setStoryOpen] = useState(false);
  // When we can't reliably escape the iOS FB/IG webview, we keep the Stripe URL
  // here to render a short "open in Safari" hint with a manual retry button.
  const [breakoutUrl, setBreakoutUrl] = useState(null);

  // Source + language the visitor arrived with (organic landing vs paid ad).
  const entry = readEntry();
  const src = entry.src;
  // Copy language: an organic-landing lang param wins (so a Lithuanian parent
  // who was just reading the LT landing sees an LT paywall); otherwise fall back
  // to the /fb/:lang funnel language, then English. Only languages present in
  // COPY are honoured, so a stray ?lang=de can never blank the page.
  const displayLang =
    (src === 'landing' && COPY[entry.lang] && entry.lang) ||
    (COPY[funnelLang] && funnelLang) ||
    'en';
  const c = COPY[displayLang];

  useEffect(() => {
    // Attribute the whole funnel to its acquisition source. Registering as a
    // super property means payment_abandoned, the breakout events, and the
    // eventual purchase events on /fb/success all carry acq_source for free.
    try {
      if (src) posthog.register({ acq_source: src });
      if (src === 'landing') posthog.register({ funnel_lang: displayLang });
    } catch {
      /* best-effort */
    }
    try {
      const raw = sessionStorage.getItem('ttt_fb_answers');
      if (raw) setAnswers(JSON.parse(raw));
    } catch {
      /* ignore */
    }
    // Abandoned checkout: a leftover purchase stash means the visitor left for
    // Stripe and came back unpaid (a paid return lands on /fb/success, which
    // clears it). Clear it so this fires once and count it. We do not offer a
    // softer plan here: one product, one price, billed today.
    // Also re-check on pageshow: iOS restores the page from bfcache on
    // back-navigation, so mount effects alone would miss the common case.
    const checkAbandoned = () => {
      try {
        const stash = sessionStorage.getItem('ttt_fb_purchase');
        if (!stash) return;
        sessionStorage.removeItem('ttt_fb_purchase');
        const p = JSON.parse(stash);
        track('payment_abandoned', { currency: p?.currency, price: p?.price, funnel: 'fb' });
      } catch {
        /* ignore */
      }
    };
    checkAbandoned();
    const onPageShow = (e) => { if (e.persisted) checkAbandoned(); };
    window.addEventListener('pageshow', onPageShow);
    track('fb_pricing_viewed');
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Tie the PostHog person to the account once the session resolves (Google
  // sign-in has no stashed email, so identify here when the user lands authed).
  // identify() attaches PII, so - like the rest of the funnel - it only runs
  // with cookie consent; without it the visitor stays anonymous in PostHog.
  useEffect(() => {
    let consented = false;
    try {
      consented = localStorage.getItem('ttt_cookie_consent') === 'accepted';
    } catch {
      /* treat as no consent */
    }
    if (user?.email && consented) {
      try {
        posthog.identify(user.email, { fb_funnel: true });
      } catch {
        /* best-effort */
      }
    }
  }, [user?.email]);

  // Organic landing visitors have no stashed FB answers, so the fallback must be
  // localized (c.yourChild), not the hardcoded English 'your child'.
  const name = answers?.name?.trim() || c.yourChild;
  // For "children like ___" the generic fallback must read naturally: "children
  // like yours", never "children like your child".
  const childLike = answers?.name?.trim() || c.childLike;
  // Prefer the authenticated account email (Google sign-in has no stashed
  // email); fall back to the email captured on the onboarding step.
  const email = (user?.email || answers?.email || '').trim();
  const cur = CURRENCY[currency];

  // Hand off to Stripe. In a normal browser this is a plain same-window
  // navigation (unchanged behaviour). Inside the Facebook / Instagram /
  // Messenger in-app browser the same-window handoff crashes ("Error invoking
  // postMessage ...") and there is no Apple Pay / Google Pay, so instead we
  // break out to the device's real browser and open Stripe there. On Android
  // that reliably lands in Chrome via an intent:// URL; on iOS there is no
  // reliable programmatic escape, so we also surface a short "open in Safari"
  // hint (window.open is still attempted first, FB sometimes honours it).
  const launchCheckout = (href) => {
    if (isInAppBrowser()) {
      const res = breakOutToExternalBrowser(href);
      track('checkout_webview_breakout', { platform: res.platform, method: res.method });
      if (res.needsInstructions) {
        track('checkout_webview_breakout', { platform: res.platform, method: 'instructions_shown' });
        setBreakoutUrl(href);
      }
      return;
    }
    window.location.href = href;
  };

  // Build the Stripe client_reference_id. Prefer the app user id (app_<id>) so
  // the webhook maps the subscription to the exact account; otherwise fall back
  // to a source tag ('landing' for organic, 'fb' for ads). If the visitor came
  // through a referral link, append __ref_<code> - the webhook's
  // parseClientReferenceId reads that to credit the referrer and safely ignores
  // an unknown leading tag like 'landing'/'fb'.
  const buildClientRef = () => {
    let refCode = null;
    try { refCode = sessionStorage.getItem('ttt_ref_code'); } catch { /* ignore */ }
    const base = user?.id ? `app_${user.id}` : (src === 'landing' ? 'landing' : 'fb');
    return refCode ? `${base}__ref_${refCode}` : base;
  };

  const handleStart = () => {
    track('fb_pricing_cta_tapped', { currency });
    track('payment_initiated', { currency, price: cur.price, funnel: 'fb' });
    trackPixel('InitiateCheckout', { value: cur.price, currency });
    // Stash the chosen price so /fb/success can fire the Purchase pixel with the
    // correct value/currency when the user returns from Stripe.
    try {
      sessionStorage.setItem('ttt_fb_purchase', JSON.stringify({ currency, price: cur.price }));
    } catch {
      /* ignore */
    }
    let href = FB_STRIPE_LINK;
    try {
      const u = new URL(href);
      if (email) u.searchParams.set('prefilled_email', email);
      // Tag the checkout so the Stripe webhook can map the subscription back to
      // this exact account. We now create the account before this step, so use
      // the app user id when present (same app_<id> convention as the main
      // paywall); otherwise fall back to a source tag ('landing' for organic,
      // 'fb' for ads) + the prefilled email. If the visitor arrived via a
      // referral link the code is appended as __ref_<code> so the webhook's
      // parseClientReferenceId credits the referrer (it safely ignores it here).
      u.searchParams.set('client_reference_id', buildClientRef());
      href = u.toString();
    } catch {
      /* fall back to bare link */
    }
    launchCheckout(href);
  };

  return (
    // The document itself is the scroller: min-h (not fixed height) so the page
    // grows with its content and scrolls to the very bottom on iOS Safari,
    // Android Chrome AND the Facebook / Instagram in-app webviews. Using dvh
    // (dynamic viewport) instead of fixed 100vh / svh means the layout tracks
    // the *visible* area, so it never extends behind the FB/IG bottom toolbar
    // the way `fixed inset-0` did (which trapped the CTA off-screen). The CTA
    // lives in a normal-flow flex-shrink-0 footer below the body, so it can
    // never overlay the last content and is always reachable.
    <div
      className="fb-funnel min-h-[100dvh] flex flex-col"
      style={{
        backgroundColor: FB.cream,
        fontFamily: FB.font,
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <Helmet>
        <title>Timo Helps</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Content flows in the document; the page (not this div) scrolls. */}
      <div className="flex-1 px-5 pt-5">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mx-auto w-full max-w-md"
        >
          {/* Hero: Timo with a child, above the price. */}
          <ImageSlot src={timoWithKid} ratio="16 / 10" className="mb-5" />
          <h1 className="font-semibold text-[26px] leading-tight text-center mb-2" style={{ color: FB.ink }}>
            {fill(c.heroTitle, { name })}
          </h1>
          <p className="text-[15.5px] leading-snug text-center mb-6" style={{ color: '#6B6472' }}>
            {fill(c.heroSubtitle, { name })}
          </p>

          {/* Benefits */}
          <div className="space-y-3 mb-6">
            {BENEFITS.map(({ icon: Icon, text }, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.07, duration: 0.3 }}
                className="flex items-center gap-3 bg-white rounded-[18px] px-4 py-3 shadow-[0_2px_10px_rgba(45,49,66,0.04)]"
              >
                <span
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${FB.orange}1A` }}
                >
                  <Icon size={20} weight="bold" color={FB.orange} />
                </span>
                <span className="text-[14.5px] leading-snug font-medium" style={{ color: FB.ink }}>
                  {c.benefits?.[i] || text}
                </span>
              </motion.div>
            ))}
          </div>

          {/* Social proof row */}
          <div className="flex items-center justify-center gap-6 mb-6">
            <div className="text-center">
              <p className="font-semibold text-[20px] leading-none" style={{ color: FB.ink }}>
                10,000+
              </p>
              <p className="text-[12px] mt-1" style={{ color: '#9A93A2' }}>
                {c.families}
              </p>
            </div>
            <div className="w-px h-8" style={{ backgroundColor: FB.track }} />
            <div className="text-center">
              <div className="flex items-center justify-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={15} weight="fill" color="#FFC23C" />
                ))}
              </div>
              <p className="text-[12px] mt-1" style={{ color: '#9A93A2' }}>
                {c.rating}
              </p>
            </div>
          </div>

          {/* Plan card */}
          <div
            className="rounded-[24px] p-5 mb-5"
            style={{ backgroundColor: '#FFFFFF', border: `2px solid ${FB.orange}`, boxShadow: '0 8px 26px rgba(255,140,90,0.18)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-[17px]" style={{ color: FB.ink }}>
                {c.planTitle}
              </span>
              {/* currency switch */}
              <div className="flex rounded-full p-0.5" style={{ backgroundColor: FB.cream }}>
                {Object.keys(CURRENCY).map((c) => (
                  <button
                    key={c}
                    onClick={() => { setCurrency(c); track('fb_pricing_currency_changed', { currency: c }); }}
                    className="px-2.5 py-1 rounded-full text-[12px] font-semibold touch-manipulation"
                    style={{
                      backgroundColor: currency === c ? FB.orange : 'transparent',
                      color: currency === c ? '#fff' : '#9A93A2',
                    }}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-end gap-1 mb-1">
              <span className="font-semibold text-[40px] leading-none" style={{ color: FB.ink }}>
                {cur.symbol}{cur.price}
              </span>
              <span className="text-[15px] mb-1" style={{ color: '#9A93A2' }}>
                {cur.per}
              </span>
            </div>
            <p className="text-[13.5px]" style={{ color: '#6B6472' }}>
              {fill(c.planNote, { price: `${cur.symbol}${cur.price}` })}
            </p>
          </div>

          {/* Testimonial */}
          <div className="rounded-[22px] p-5 mb-6" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 10px rgba(45,49,66,0.04)' }}>
            <div className="flex gap-0.5 mb-2">
              {Array.from({ length: REVIEWS[0].stars }).map((_, i) => (
                <Star key={i} size={15} weight="fill" color="#FFC23C" />
              ))}
            </div>
            <p className="text-[15px] leading-snug font-medium mb-2" style={{ color: FB.ink }}>
              “{c.review.text}”
            </p>
            <p className="text-[13px]" style={{ color: '#9A93A2' }}>
              {c.review.author}
            </p>
          </div>

          {/* From the founder: a real-person trust signal right before the CTA.
              Collapsed to a friendly one-liner so it never stalls momentum;
              parents who want the "why" can tap to read the full story. */}
          <div
            className="rounded-[22px] p-5 mb-6"
            style={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 10px rgba(45,49,66,0.04)' }}
          >
            <button
              type="button"
              onClick={() => {
                setStoryOpen((o) => {
                  if (!o) track('fb_pricing_founder_story_opened');
                  return !o;
                });
              }}
              className="w-full flex items-center gap-4 text-left touch-manipulation"
            >
              <img
                src={founderPhoto}
                alt="Gintaras, founder"
                draggable="false"
                className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                style={{ boxShadow: '0 2px 8px rgba(45,49,66,0.14)' }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-[14.5px] font-semibold leading-snug" style={{ color: FB.ink }}>
                  {c.founderTeaser}
                </p>
                <p className="text-[13px] font-medium mt-0.5" style={{ color: FB.orange }}>
                  {storyOpen ? c.founderClose : c.founderRead}
                </p>
              </div>
              <CaretDown
                size={18}
                weight="bold"
                color={FB.orange}
                style={{ transform: storyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s' }}
              />
            </button>

            <AnimatePresence initial={false}>
              {storyOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <div className="pt-4 space-y-3 text-[14px] leading-relaxed" style={{ color: FB.ink }}>
                    <p>{c.founderP1}</p>
                    <p>{fill(c.founderP2, { child: childLike })}</p>
                    <p>{c.founderP3}</p>
                  </div>
                  <p className="mt-4 text-[13px] font-semibold" style={{ color: FB.orange }}>
                    {c.founderSign}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* CTA footer. A normal-flow flex-shrink-0 sibling below the body: on a
          short page it rests at the bottom of the visible dvh area (above the
          FB/IG toolbar); on a tall page it flows at the natural end and the
          document scrolls to reach it. paddingBottom keeps a guaranteed minimum
          buffer even where env(safe-area-inset-bottom) reports 0 (FB/IG
          webviews) so the button never lands under the browser chrome. */}
      <div
        className="flex-shrink-0 px-5 pt-2"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 20px)' }}
      >
        <div className="mx-auto w-full max-w-md">
          <FbButton onClick={handleStart}>{c.ctaStart}</FbButton>
          <p className="mt-3 text-[11px] text-center leading-snug" style={{ color: '#A79E95' }}>
            {c.legalPre}{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">{c.legalTerms}</a> {c.legalAnd}{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">{c.legalPrivacy}</a>.
          </p>
        </div>
      </div>
      {/* iOS in-app-browser breakout hint. Facebook / Instagram on iOS give no
          reliable way to open the real browser from JS, so when the automatic
          attempt can't be trusted we show a short, friendly instruction with a
          manual retry. English copy: the /fb funnel targets US/UK. */}
      {breakoutUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center px-4"
          style={{
            backgroundColor: 'rgba(45,49,66,0.55)',
            // The sheet is bottom-anchored, so on a notched iPhone a flat pb-6
            // put its Close row inside the home indicator's swipe zone. This is
            // the last screen before a lost sale, so both of its buttons have to
            // be reachable.
            paddingBottom: 'max(1.5rem, calc(0.75rem + env(safe-area-inset-bottom, 0px)))',
          }}
          onClick={() => setBreakoutUrl(null)}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md rounded-[24px] p-6 text-center"
            style={{ backgroundColor: '#fff' }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-semibold text-[18px] mb-2" style={{ color: FB.ink }}>
              {c.breakoutTitle}
            </p>
            <p className="text-[14.5px] leading-snug mb-4" style={{ color: '#6B6472' }}>
              {c.breakoutPre}
              <span className="font-semibold" style={{ color: FB.ink }}> ••• </span>
              {c.breakoutMid}
              <span className="font-semibold" style={{ color: FB.ink }}> {c.breakoutOpen} </span>
              {c.breakoutPost}
            </p>
            <button
              type="button"
              onClick={() => {
                track('checkout_webview_breakout', { platform: 'ios', method: 'window_open' });
                try { window.open(breakoutUrl, '_blank'); } catch { /* ignore */ }
              }}
              className="w-full py-3.5 rounded-full font-semibold text-[15.5px] touch-manipulation mb-2"
              style={{ backgroundColor: FB.orange, color: '#fff' }}
            >
              {c.breakoutRetry}
            </button>
            <button
              type="button"
              onClick={() => setBreakoutUrl(null)}
              className="w-full min-h-[44px] py-2 text-[13.5px] font-medium touch-manipulation"
              style={{ color: '#9A93A2' }}
            >
              {c.breakoutClose}
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
